import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SocialConnection } from '@nucleus-commerce/api-client/admin';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { toEnableAdsBody } from '../api/mappers';
import { useDisableAds, useEnableAds } from '../hooks/use-ads-mutations';
import {
  useDisableCatalog,
  useEnableCatalog,
  useIngestCatalog,
} from '../hooks/use-catalog-mutations';
import { useConnectSocial, useDisconnectSocial } from '../hooks/use-connection-mutations';
import { useSocialCatalog } from '../hooks/use-social-catalog';
import { useClaimWebsite } from '../hooks/use-website-mutations';
import { connectionState } from '../schemas/channel-state';

import { AdAccountDialog } from './ad-account-dialog';
import { AdsStep } from './ads-step';
import { CatalogStep } from './catalog-step';
import { ConnectionStep } from './connection-step';
import { DisconnectDialog } from './disconnect-dialog';
import { WebsiteStep } from './website-step';

const PROVIDER = 'pinterest';

export interface PinterestCardProps {
  storeId: string;
  connection: SocialConnection | undefined;
  /** The store's primary domain, the website the claim step claims; undefined while loading. */
  primaryDomain: string | null | undefined;
  editable: boolean;
  timeZone: string;
}

/** Pinterest's setup: connection, website claim, catalogue sync and the ads step, in that order. */
export function PinterestCard({
  storeId,
  connection,
  primaryDomain,
  editable,
  timeZone,
}: PinterestCardProps) {
  const { t } = useTranslation('social');
  const name = t('pinterest.name');
  const state = connectionState(connection);
  const active = state === 'connected';

  const catalog = useSocialCatalog(storeId, PROVIDER, active);
  const connect = useConnectSocial(storeId, PROVIDER);
  const disconnect = useDisconnectSocial(storeId, PROVIDER);
  const enableCatalog = useEnableCatalog(storeId, PROVIDER);
  const disableCatalog = useDisableCatalog(storeId, PROVIDER);
  const ingest = useIngestCatalog(storeId, PROVIDER);
  const enableAds = useEnableAds(storeId, PROVIDER);
  const disableAds = useDisableAds(storeId, PROVIDER);
  const claimWebsite = useClaimWebsite(storeId, PROVIDER);

  const [disconnecting, setDisconnecting] = useState(false);
  const [choosing, setChoosing] = useState(false);

  return (
    <Card role="region" aria-label={name} className="gap-4">
      <CardHeader>
        <CardTitle className="text-[15px]">{t('setup.title')}</CardTitle>
        <CardDescription>{t('pinterest.hint')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-6">
          <ConnectionStep
            state={state}
            connection={connection}
            editable={editable}
            connecting={connect.isPending && connect.variables === 'connect'}
            onConnect={() => connect.mutate('connect')}
            onDisconnect={() => {
              disconnect.reset();
              setDisconnecting(true);
            }}
          />
          <WebsiteStep
            active={active}
            connection={connection}
            primaryDomain={primaryDomain}
            editable={editable}
            claiming={claimWebsite.isPending}
            onClaim={() => claimWebsite.mutate()}
            reconnecting={connect.isPending && connect.variables === 'connect'}
            onReconnect={() => connect.mutate('connect')}
          />
          <CatalogStep
            active={active}
            status={catalog.data}
            loading={active && catalog.isPending}
            error={catalog.error}
            onRetry={() => void catalog.refetch()}
            editable={editable}
            toggling={enableCatalog.isPending || disableCatalog.isPending}
            onToggle={(next) => (next ? enableCatalog.mutate() : disableCatalog.mutate())}
            syncing={ingest.isPending}
            onSync={() => ingest.mutate()}
            timeZone={timeZone}
            websiteClaimed={connection?.website?.status === 'verified'}
          />
          <AdsStep
            storeId={storeId}
            active={active}
            connection={connection}
            editable={editable}
            connecting={connect.isPending && connect.variables === 'ads'}
            onConnectAds={() => connect.mutate('ads')}
            onChoose={() => {
              enableAds.reset();
              setChoosing(true);
            }}
            disabling={disableAds.isPending}
            onDisable={() => disableAds.mutate()}
          />
        </ol>
      </CardContent>
      <DisconnectDialog
        open={disconnecting}
        pending={disconnect.isPending}
        error={disconnect.error}
        onConfirm={() => disconnect.mutate(undefined, { onSuccess: () => setDisconnecting(false) })}
        onOpenChange={(open) => {
          if (!open) setDisconnecting(false);
        }}
      />
      <AdAccountDialog
        open={choosing}
        onOpenChange={(open) => {
          if (!open) setChoosing(false);
        }}
        storeId={storeId}
        provider={PROVIDER}
        currentId={connection?.ads.adAccountId ?? null}
        pending={enableAds.isPending}
        error={enableAds.error}
        onConfirm={(values) =>
          enableAds.mutate(toEnableAdsBody(values), { onSuccess: () => setChoosing(false) })
        }
      />
    </Card>
  );
}
